import React, { useState, useEffect } from 'react';
import { Music2, Radio, Clock, Zap, Users, Check, ArrowRight, Menu, X, Sparkles, Mic2, Mail, Wand2, ChevronLeft, ChevronRight, Maximize2 } from 'lucide-react';
import ContactForm from './ContactForm';
import { supabase } from '../lib/supabase';
import { APP_VERSION } from '../version';

interface ScreenshotItem {
  id: string;
  title: string;
  description: string;
  image_url: string;
  category: string;
  display_order: number;
}

interface HomepageProps {
  onNavigateToAuth: (packageId?: string) => void;
  onNavigateToPricing: () => void;
}

interface PricingPackage {
  id: string;
  name: string;
  description: string | null;
  monthly_price_cents: number;
  monthly_price_if_annual_cents: number | null;
  storage_capacity_mb: number;
  true_speech_character_limit: number;
  trial_duration_days: number | null;
  trial_enabled: boolean;
  is_featured: boolean;
  is_active: boolean;
  features: string[];
}

const Homepage: React.FC<HomepageProps> = ({ onNavigateToAuth, onNavigateToPricing }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'annual'>('monthly');
  const [pricingPackages, setPricingPackages] = useState<PricingPackage[]>([]);
  const [legalDisclaimer, setLegalDisclaimer] = useState<string>('');
  const [shortFooterNotices, setShortFooterNotices] = useState<string>('');
  const [termsOfService, setTermsOfService] = useState<string>('');
  const [privacyPolicy, setPrivacyPolicy] = useState<string>('');
  const [showLegalDisclaimerModal, setShowLegalDisclaimerModal] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [loadingPricing, setLoadingPricing] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [screenshots, setScreenshots] = useState<ScreenshotItem[]>([]);

  const screenshotsWithImages = screenshots.filter(s => s.image_url);

  const openLightbox = (index: number) => {
    setLightboxIndex(index);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
  };

  const nextImage = () => {
    setLightboxIndex((prev) => (prev + 1) % screenshotsWithImages.length);
  };

  const prevImage = () => {
    setLightboxIndex((prev) => (prev - 1 + screenshotsWithImages.length) % screenshotsWithImages.length);
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!lightboxOpen) return;
      if (e.key === 'Escape') closeLightbox();
      if (e.key === 'ArrowRight') nextImage();
      if (e.key === 'ArrowLeft') prevImage();
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    fetchPricingPackages();
    fetchLegalDocuments();
    fetchScreenshots();
  }, []);

  const fetchScreenshots = async () => {
    try {
      const { data, error } = await supabase
        .from('lightbox_items')
        .select('id, title, description, image_url, category, display_order')
        .eq('is_active', true)
        .order('display_order', { ascending: true });

      if (error) throw error;
      setScreenshots(data || []);
    } catch (err) {
      console.error('Error fetching screenshots:', err);
    }
  };

  const fetchLegalDocuments = async () => {
    try {
      const { data, error } = await supabase
        .from('legal_documents')
        .select('document_type, content')
        .in('document_type', ['legal_disclaimer', 'short_footer_notices', 'terms_of_service', 'privacy_policy'])
        .eq('is_active', true);

      if (error) throw error;

      console.log('Legal documents fetched:', data);

      data?.forEach((doc) => {
        if (doc.document_type === 'legal_disclaimer') {
          setLegalDisclaimer(doc.content);
          console.log('Set legal_disclaimer:', doc.content.substring(0, 50));
        } else if (doc.document_type === 'short_footer_notices') {
          setShortFooterNotices(doc.content);
          console.log('Set short_footer_notices:', doc.content);
        } else if (doc.document_type === 'terms_of_service') {
          setTermsOfService(doc.content);
          console.log('Set terms_of_service:', doc.content.substring(0, 50));
        } else if (doc.document_type === 'privacy_policy') {
          setPrivacyPolicy(doc.content);
          console.log('Set privacy_policy:', doc.content.substring(0, 50));
        }
      });
    } catch (err) {
      console.error('Error fetching legal documents:', err);
    }
  };

  const fetchPricingPackages = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_packages')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (error) throw error;
      setPricingPackages(data || []);
    } catch (error) {
      console.error('Error fetching pricing packages:', error);
    } finally {
      setLoadingPricing(false);
    }
  };

  const getFreeTrialPackageId = () => {
    return pricingPackages.find(pkg => pkg.name === 'Free Trial' || pkg.monthly_price_cents === 0)?.id;
  };

  const features = [
    {
      icon: <Music2 className="h-8 w-8" />,
      title: 'Smart Playlists',
      description: 'Create and manage unlimited playlists with intelligent organization.',
      gradient: 'from-orange-500 to-red-600'
    },
    {
      icon: <Wand2 className="h-8 w-8" />,
      title: 'Create Ads Smarter: AI Voices + Music',
      description: 'Create professional ads with AI! Select a voice from multiple choices in any language, and let AI generate custom background music for your ads.',
      gradient: 'from-rose-500 to-pink-600'
    },
    {
      icon: <Mic2 className="h-8 w-8" />,
      title: 'AI Voice Announcements',
      description: 'Generate professional time and custom announcements using advanced AI voice technology.',
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      icon: <Clock className="h-8 w-8" />,
      title: 'Advanced Scheduling',
      description: 'Set up date ranges and periodic time windows for automated playlist management.',
      gradient: 'from-yellow-500 to-orange-600'
    },
    {
      icon: <Radio className="h-8 w-8" />,
      title: 'Ad Scheduling',
      description: 'Schedule ads by track count or time interval with flexible recurring schedules.',
      gradient: 'from-amber-500 to-orange-600'
    },
    {
      icon: <Zap className="h-8 w-8" />,
      title: 'Smart Skip Technology',
      description: 'Automatically detects and skips trailing and ending silence for seamless playback experience.',
      gradient: 'from-green-500 to-emerald-600'
    }
  ];

  const stats = [
    { label: 'Active Users', value: '10,000+', icon: <Users className="h-6 w-6" /> },
    { label: 'AI Ads Created', value: '100K+', icon: <Wand2 className="h-6 w-6" /> },
    { label: 'AI Announcements', value: '250K+', icon: <Mic2 className="h-6 w-6" /> }
  ];

  const formatPrice = (cents: number) => {
    return (cents / 100).toFixed(2);
  };

  const calculateVAT = (cents: number) => {
    return (cents * 1.19 / 100).toFixed(2);
  };

  const getDisplayPrice = (pkg: PricingPackage) => {
    if (billingPeriod === 'annual' && pkg.monthly_price_if_annual_cents) {
      return pkg.monthly_price_if_annual_cents;
    }
    return pkg.monthly_price_cents;
  };

  return (
    <div className="min-h-screen bg-black text-white overflow-x-hidden">
      <header>
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? 'bg-black/80 backdrop-blur-lg border-b border-white/10' : 'bg-transparent'
      }`} aria-label="Main navigation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex items-center space-x-3">
              <div className="relative">
                <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg blur opacity-50"></div>
                <div className="relative bg-gradient-to-r from-orange-500 to-orange-600 p-2 rounded-lg">
                  <Music2 className="h-6 w-6 text-black" />
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-bold bg-gradient-to-r from-orange-500 to-orange-600 bg-clip-text text-transparent">
                  PlexyMusic
                </span>
                <span className="text-xs font-mono text-white/40 bg-white/5 px-2 py-0.5 rounded-md border border-white/10">
                  {APP_VERSION}
                </span>
              </div>
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:flex items-center space-x-8">
              <a href="#features" className="text-white/80 hover:text-white transition-colors">
                Features
              </a>
              <a href="#pricing" className="text-white/80 hover:text-white transition-colors">
                Pricing
              </a>
              <a href="#contact" className="text-white/80 hover:text-white transition-colors">
                Contact
              </a>
              <button
                onClick={() => onNavigateToAuth()}
                className="text-white/80 hover:text-white transition-colors"
              >
                Sign In
              </button>
              <button
                onClick={() => onNavigateToAuth(getFreeTrialPackageId())}
                className="bg-gradient-to-r from-orange-500 to-orange-600 text-black px-6 py-2.5 rounded-lg font-semibold hover:shadow-lg hover:shadow-orange-500/50 transform hover:-translate-y-0.5 transition-all duration-200"
              >
                Start Free Trial
              </button>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="md:hidden text-white p-2"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-black/95 backdrop-blur-lg border-b border-white/10">
            <div className="px-4 py-6 space-y-4">
              <a href="#features" className="block text-white/80 hover:text-white py-2">
                Features
              </a>
              <a href="#pricing" className="block text-white/80 hover:text-white py-2">
                Pricing
              </a>
              <a href="#contact" className="block text-white/80 hover:text-white py-2">
                Contact
              </a>
              <button
                onClick={() => onNavigateToAuth()}
                className="block w-full text-left text-white/80 hover:text-white py-2"
              >
                Sign In
              </button>
              <button
                onClick={() => onNavigateToAuth(getFreeTrialPackageId())}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 text-black px-6 py-3 rounded-lg font-semibold"
              >
                Start Free Trial
              </button>
            </div>
          </div>
        )}
      </nav>
      </header>

      <main>
      <section className="relative pt-32 pb-20 px-4 sm:px-6 lg:px-8 overflow-hidden" aria-label="Hero">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500/10 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-orange-600/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-gradient-to-r from-orange-500/10 to-orange-600/10 border border-orange-500/30 rounded-full px-4 py-2 mb-8 animate-fade-in-down">
              <Sparkles className="h-4 w-4 text-orange-500" />
              <span className="text-sm font-medium text-orange-400">AI-Powered Music, Ads & Announcements Automation</span>
            </div>

            <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-6 animate-fade-in-up">
              <span className="bg-gradient-to-r from-white via-orange-100 to-white bg-clip-text text-transparent">
                Automate Your Background Venue Music, Ads and Announcements
              </span>
              <br />
              <span className="bg-gradient-to-r from-orange-500 via-orange-600 to-orange-500 bg-clip-text text-transparent animate-gradient">
                With AI
              </span>
            </h1>

            <p className="text-xl text-white/70 mb-12 max-w-3xl mx-auto animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              Create professional playlists with AI voice announcements, AI Ads, smart scheduling, and seamless playback.
              Perfect for retail shops, hotels, coffee shops, supermarkets and any other public area.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 animate-fade-in-up" style={{ animationDelay: '0.4s' }}>
              <button
                onClick={() => onNavigateToAuth(getFreeTrialPackageId())}
                className="w-full sm:w-auto bg-gradient-to-r from-orange-500 to-orange-600 text-black px-8 py-4 rounded-lg font-bold text-lg hover:shadow-2xl hover:shadow-orange-500/50 transform hover:-translate-y-1 transition-all duration-200 animate-pulse-glow"
              >
                Start Free Trial
                <ArrowRight className="inline-block ml-2 h-5 w-5" />
              </button>
              <button
                onClick={() => document.getElementById('features')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full sm:w-auto bg-white/10 backdrop-blur-sm border border-white/20 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white/20 transform hover:-translate-y-1 transition-all duration-200"
              >
                Learn More
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-12 px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-4xl mx-auto">
            {stats.map((stat, index) => (
              <div
                key={index}
                className="glass-effect p-6 rounded-xl text-center transform hover:scale-105 transition-all duration-200"
              >
                <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-r from-orange-500/20 to-orange-600/20 rounded-full mb-3">
                  <div className="text-orange-500">{stat.icon}</div>
                </div>
                <div className="text-3xl font-bold mb-1">{stat.value}</div>
                <div className="text-white/60 text-sm">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="features" className="py-24 px-4 sm:px-6 lg:px-8 relative" aria-labelledby="features-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 id="features-heading" className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-orange-400 bg-clip-text text-transparent">
                Powerful Features
              </span>
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Everything you need to create, manage, and automate professional music playlists. Easily create AI ads to promote your products and services.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="group glass-effect p-8 rounded-2xl hover:bg-white/5 transform hover:-translate-y-2 transition-all duration-300"
              >
                <div className={`inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r ${feature.gradient} rounded-xl mb-6 transform group-hover:scale-110 transition-transform duration-300`}>
                  <div className="text-white">{feature.icon}</div>
                </div>
                <h3 className="text-xl font-bold mb-3">{feature.title}</h3>
                <p className="text-white/70">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="pricing" className="py-24 px-4 sm:px-6 lg:px-8 relative" aria-labelledby="pricing-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 id="pricing-heading" className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-orange-400 bg-clip-text text-transparent">
                Simple, Transparent Pricing
              </span>
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto mb-8">
              Choose the perfect plan for your needs. Start with a free trial and upgrade anytime.
            </p>

            {/* Billing Toggle */}
            <div className="inline-flex items-center space-x-3 bg-white/5 backdrop-blur-sm border border-white/10 rounded-full p-1">
              <button
                onClick={() => setBillingPeriod('monthly')}
                className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-200 ${
                  billingPeriod === 'monthly'
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-black'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Monthly
              </button>
              <button
                onClick={() => setBillingPeriod('annual')}
                className={`px-6 py-2.5 rounded-full font-semibold transition-all duration-200 ${
                  billingPeriod === 'annual'
                    ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-black'
                    : 'text-white/70 hover:text-white'
                }`}
              >
                Annual
                <span className="ml-1.5 text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Save</span>
              </button>
            </div>
          </div>

          {loadingPricing ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {pricingPackages.map((pkg, index) => {
              const displayPrice = getDisplayPrice(pkg);
              const hasAnnualPrice = pkg.monthly_price_if_annual_cents && pkg.monthly_price_if_annual_cents > 0;

              return (
                <div
                  key={pkg.id}
                  className={`relative group ${pkg.is_featured ? 'lg:-translate-y-4' : ''}`}
                >
                  {pkg.is_featured && (
                    <div className="absolute -top-4 left-1/2 transform -translate-x-1/2 z-10">
                      <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-black px-4 py-1 rounded-full text-sm font-bold">
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div
                    className={`relative h-full glass-effect rounded-2xl p-8 transition-all duration-300 ${
                      pkg.is_featured
                        ? 'border-2 border-orange-500/50 shadow-2xl shadow-orange-500/20'
                        : 'border border-white/10 hover:border-white/20'
                    } hover:transform hover:-translate-y-2`}
                  >
                    <h3 className="text-2xl font-bold mb-2">{pkg.name}</h3>
                    <p className="text-white/60 text-sm mb-6">{pkg.description}</p>

                    <div className="mb-6">
                      {pkg.trial_enabled && pkg.trial_duration_days ? (
                        <div>
                          <div className="text-4xl font-bold mb-1">Free</div>
                          <div className="text-white/60 text-sm">{pkg.trial_duration_days} days trial</div>
                        </div>
                      ) : (
                        <div>
                          <div className="flex items-baseline mb-1">
                            <span className="text-4xl font-bold">€{formatPrice(displayPrice)}</span>
                            <span className="text-white/60 ml-2">/month</span>
                          </div>
                          <div className="text-white/40 text-xs">
                            €{calculateVAT(displayPrice)} with VAT
                          </div>
                          {billingPeriod === 'annual' && hasAnnualPrice && (
                            <div className="text-green-400 text-xs mt-1">
                              Billed annually
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    <ul className="space-y-3 mb-8">
                      {pkg.features && pkg.features.map((feature, featureIndex) => (
                        <li key={featureIndex} className="flex items-start">
                          <div className="flex-shrink-0 w-5 h-5 bg-orange-500/20 rounded-full flex items-center justify-center mt-0.5">
                            <Check className="h-3 w-3 text-orange-500" />
                          </div>
                          <span className="ml-3 text-white/80 text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    <button
                      onClick={() => onNavigateToAuth(pkg.id)}
                      className={`w-full py-3 rounded-lg font-semibold transition-all duration-200 ${
                        pkg.is_featured
                          ? 'bg-gradient-to-r from-orange-500 to-orange-600 text-black hover:shadow-lg hover:shadow-orange-500/50 transform hover:-translate-y-1'
                          : 'bg-white/10 text-white hover:bg-white/20 border border-white/20'
                      }`}
                    >
                      Get Started
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
          )}

          <div className="mt-16 text-center">
            <div className="inline-flex flex-wrap items-center justify-center gap-8 text-white/40 text-sm">
              <div className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-green-500" />
                <span>No credit card required</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-green-500" />
                <span>Cancel anytime</span>
              </div>
              <div className="flex items-center space-x-2">
                <Check className="h-4 w-4 text-green-500" />
                <span>Instant activation</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Screenshots Gallery Section */}
      {screenshotsWithImages.length > 0 && (
      <section id="gallery" className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-orange-500/5 rounded-full blur-3xl"></div>
          <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-orange-600/5 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-orange-400 bg-clip-text text-transparent">
                Packed with Features, Sleek Design, and Intuitive UI
              </span>
            </h2>
            <p className="text-xl text-white/70 max-w-3xl mx-auto">
              Experience a beautifully crafted interface designed for professionals. Every detail has been carefully considered for the best user experience.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {screenshotsWithImages.map((screenshot, index) => (
              <div
                key={screenshot.id}
                onClick={() => openLightbox(index)}
                className="group relative cursor-pointer"
                style={{
                  perspective: '1000px'
                }}
              >
                <div
                  className="relative rounded-xl overflow-hidden bg-gradient-to-br from-gray-800 to-gray-900 border border-white/10 shadow-xl transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-orange-500/20"
                  style={{
                    transform: 'rotateX(2deg) rotateY(-2deg)',
                    transformStyle: 'preserve-3d',
                    transition: 'transform 0.5s ease'
                  }}
                  onMouseEnter={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'rotateX(0deg) rotateY(0deg) translateZ(20px) scale(1.02)';
                  }}
                  onMouseLeave={(e) => {
                    (e.currentTarget as HTMLElement).style.transform = 'rotateX(2deg) rotateY(-2deg)';
                  }}
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={screenshot.image_url}
                      alt={screenshot.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent opacity-60 group-hover:opacity-90 transition-opacity duration-300"></div>

                    <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300">
                      <div className="bg-white/20 backdrop-blur-sm p-3 rounded-full transform scale-50 group-hover:scale-100 transition-transform duration-300">
                        <Maximize2 className="h-6 w-6 text-white" />
                      </div>
                    </div>

                    <div className="absolute bottom-0 left-0 right-0 p-4 transform translate-y-2 group-hover:translate-y-0 transition-transform duration-300">
                      <span className="inline-block text-xs font-semibold text-orange-400 bg-orange-500/20 px-2 py-1 rounded-full mb-2">
                        {screenshot.category}
                      </span>
                      <h3 className="text-white font-bold text-sm md:text-base truncate">
                        {screenshot.title}
                      </h3>
                      <p className="text-white/60 text-xs mt-1 line-clamp-2 hidden md:block">
                        {screenshot.description}
                      </p>
                    </div>
                  </div>
                </div>

                <div
                  className="absolute inset-0 rounded-xl bg-gradient-to-r from-orange-500/0 via-orange-500/10 to-orange-500/0 opacity-0 group-hover:opacity-100 blur-xl transition-opacity duration-500 -z-10"
                ></div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      {/* Lightbox Modal */}
      {lightboxOpen && screenshotsWithImages.length > 0 && screenshotsWithImages[lightboxIndex] && (
        <div
          className="fixed inset-0 bg-black/95 backdrop-blur-sm z-50 flex items-center justify-center"
          onClick={closeLightbox}
        >
          <button
            onClick={(e) => {
              e.stopPropagation();
              closeLightbox();
            }}
            className="absolute top-4 right-4 p-3 text-white/60 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 z-50"
          >
            <X className="h-6 w-6" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              prevImage();
            }}
            className="absolute left-4 md:left-8 p-3 text-white/60 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 z-50"
          >
            <ChevronLeft className="h-8 w-8" />
          </button>

          <button
            onClick={(e) => {
              e.stopPropagation();
              nextImage();
            }}
            className="absolute right-4 md:right-8 p-3 text-white/60 hover:text-white bg-white/10 hover:bg-white/20 rounded-full transition-all duration-200 z-50"
          >
            <ChevronRight className="h-8 w-8" />
          </button>

          <div
            className="max-w-6xl w-full mx-4 md:mx-8"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl shadow-black/50 border border-white/10">
              <img
                src={screenshotsWithImages[lightboxIndex]?.image_url}
                alt={screenshotsWithImages[lightboxIndex]?.title}
                className="w-full h-auto max-h-[80vh] object-contain bg-gray-900"
              />

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-6 md:p-8">
                <span className="inline-block text-sm font-semibold text-orange-400 bg-orange-500/20 px-3 py-1 rounded-full mb-3">
                  {screenshotsWithImages[lightboxIndex]?.category}
                </span>
                <h3 className="text-white font-bold text-xl md:text-2xl mb-2">
                  {screenshotsWithImages[lightboxIndex]?.title}
                </h3>
                <p className="text-white/70 text-sm md:text-base max-w-2xl">
                  {screenshotsWithImages[lightboxIndex]?.description}
                </p>
              </div>
            </div>

            <div className="flex items-center justify-center mt-6 space-x-2">
              {screenshotsWithImages.map((_, index) => (
                <button
                  key={index}
                  onClick={(e) => {
                    e.stopPropagation();
                    setLightboxIndex(index);
                  }}
                  className={`w-2 h-2 rounded-full transition-all duration-200 ${
                    index === lightboxIndex
                      ? 'bg-orange-500 w-6'
                      : 'bg-white/30 hover:bg-white/50'
                  }`}
                />
              ))}
            </div>

            <div className="text-center mt-4 text-white/40 text-sm">
              {lightboxIndex + 1} / {screenshotsWithImages.length}
            </div>
          </div>
        </div>
      )}

      <section id="contact" className="py-24 px-4 sm:px-6 lg:px-8 relative" aria-labelledby="contact-heading">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl mb-6">
              <Mail className="h-8 w-8 text-white" />
            </div>
            <h2 id="contact-heading" className="text-4xl sm:text-5xl font-bold mb-4">
              <span className="bg-gradient-to-r from-white to-orange-400 bg-clip-text text-transparent">
                Get in Touch
              </span>
            </h2>
            <p className="text-xl text-white/70 max-w-2xl mx-auto">
              Have questions or need help? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <ContactForm />
        </div>
      </section>

      <section className="py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden" aria-label="Call to action">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/20 to-orange-600/20"></div>
        </div>
        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-4xl sm:text-5xl font-bold mb-6">
            Ready to elevate the music experience in your venue?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Start your free trial today. No credit card required.
          </p>
          <button
            onClick={() => onNavigateToAuth()}
            className="bg-white text-black px-12 py-4 rounded-lg font-bold text-lg hover:shadow-2xl transform hover:-translate-y-1 transition-all duration-200"
          >
            Get Started Free
          </button>
        </div>
      </section>
      </main>

      <footer className="border-t border-white/10 py-12 px-4 sm:px-6 lg:px-8" role="contentinfo">
        <div className="max-w-7xl mx-auto space-y-8">
          <div className="flex flex-col items-center space-y-4">
            <div className="flex items-center space-x-3">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 p-2 rounded-lg">
                <Music2 className="h-5 w-5 text-black" />
              </div>
              <span className="text-xl font-bold">PlexyMusic</span>
              <span className="text-xs font-mono text-white/40 bg-white/5 px-2 py-0.5 rounded-md border border-white/10">
                {APP_VERSION}
              </span>
            </div>
            <div className="text-white/60 text-sm text-center">
              © 2025 PlexyMusic. All rights reserved.
            </div>
          </div>

          <div className="text-white/70 text-sm max-w-4xl mx-auto text-center leading-relaxed">
            {shortFooterNotices ? (
              shortFooterNotices.split(/(Terms|Privacy Policy)/g).map((part, index) => {
                if (part === 'Terms') {
                  return (
                    <button
                      key={index}
                      onClick={() => setShowTermsModal(true)}
                      className="text-orange-400 hover:text-orange-300 underline transition-colors font-medium cursor-pointer bg-transparent border-none p-0"
                    >
                      {part}
                    </button>
                  );
                } else if (part === 'Privacy Policy') {
                  return (
                    <button
                      key={index}
                      onClick={() => setShowPrivacyModal(true)}
                      className="text-orange-400 hover:text-orange-300 underline transition-colors font-medium cursor-pointer bg-transparent border-none p-0"
                    >
                      {part}
                    </button>
                  );
                }
                return <span key={index}>{part}</span>;
              })
            ) : (
              <span className="text-white/40">Loading footer notices...</span>
            )}
          </div>

          <div className="text-center">
            {legalDisclaimer ? (
              <button
                onClick={() => setShowLegalDisclaimerModal(true)}
                className="text-orange-400 hover:text-orange-300 underline transition-colors font-medium cursor-pointer bg-transparent border-none text-sm"
              >
                Legal Disclaimer
              </button>
            ) : (
              <span className="text-white/40 text-sm">Loading legal disclaimer...</span>
            )}
          </div>
        </div>
      </footer>

      {/* Legal Disclaimer Modal */}
      {showLegalDisclaimerModal && legalDisclaimer && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-white/20">
            <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-gray-900">
              <h2 className="text-2xl font-bold text-white">Legal Disclaimer</h2>
              <button
                onClick={() => setShowLegalDisclaimerModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-white/80 leading-relaxed space-y-4 text-sm">
                  {legalDisclaimer.split('\n\n').map((paragraph, index) => {
                    const hasLinks = paragraph.includes('User Music Upload & Public Performance Agreement') ||
                                    paragraph.includes('Terms of Service') ||
                                    paragraph.includes('Privacy Policy');

                    if (hasLinks) {
                      const parts = paragraph.split(/(User Music Upload & Public Performance Agreement|Terms of Service|Privacy Policy)/g);
                      return (
                        <p key={index} className="mb-4">
                          {parts.map((part, i) => {
                            if (part === 'User Music Upload & Public Performance Agreement') {
                              return (
                                <span key={i} className="text-orange-400 font-medium">
                                  {part}
                                </span>
                              );
                            } else if (part === 'Terms of Service') {
                              return (
                                <span key={i} className="text-orange-400 font-medium">
                                  {part}
                                </span>
                              );
                            } else if (part === 'Privacy Policy') {
                              return (
                                <span key={i} className="text-orange-400 font-medium">
                                  {part}
                                </span>
                              );
                            }
                            return <span key={i}>{part}</span>;
                          })}
                        </p>
                      );
                    }

                    return (
                      <p key={index} className="mb-4">
                        {paragraph}
                      </p>
                    );
                  })}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end sticky bottom-0 bg-gray-900">
              <button
                onClick={() => setShowLegalDisclaimerModal(false)}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Terms of Service Modal */}
      {showTermsModal && termsOfService && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-white/20">
            <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-gray-900">
              <h2 className="text-2xl font-bold text-white">Terms of Service</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-white/80 leading-relaxed text-sm">
                  {termsOfService.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end sticky bottom-0 bg-gray-900">
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Privacy Policy Modal */}
      {showPrivacyModal && privacyPolicy && (
        <div className="fixed inset-0 bg-black/90 flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-gradient-to-br from-gray-900 to-black rounded-2xl max-w-4xl w-full max-h-[90vh] flex flex-col border border-white/20">
            <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-gray-900">
              <h2 className="text-2xl font-bold text-white">Privacy Policy</h2>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-6 md:p-8">
              <div className="prose prose-invert max-w-none">
                <div className="whitespace-pre-wrap text-white/80 leading-relaxed text-sm">
                  {privacyPolicy.split('\n\n').map((paragraph, index) => (
                    <p key={index} className="mb-4">
                      {paragraph}
                    </p>
                  ))}
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-white/10 flex justify-end sticky bottom-0 bg-gray-900">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="px-6 py-3 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Homepage;
